import { API } from "@swivl-mono/api-helper";
import { scheduleGroupsFromRows, slugifyScheduleGroupName } from "@swivl-mono/helpers";
import { STANDARD_DEFAULT_WEEKLY, ScheduleGroup, ScheduleInterface } from "@swivl-mono/types";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseScheduleGroupsResult {
  loading: boolean;
  error: string | null;
  /** Built-ins first, then the org's custom groups by label. */
  groups: ScheduleGroup[];
  refresh(): Promise<void>;
  /** Create a custom group at the company level with the standard default hours. Returns its slug, or the reason it was refused. */
  addGroup(name: string): Promise<{ ok: true; slug: string } | { ok: false; error: string }>;
  renameGroup(slug: string, label: string): Promise<void>;
  /** Removes the group's hours at every scope and its date overrides. Throws with the server's reason when refused. */
  deleteGroup(slug: string): Promise<void>;
}

/**
 * The org's schedule groups (ENG-3216 decision 50): the built-ins plus operator-created ones,
 * derived from the org's `schedule` rows. Groups are company-wide vocabulary — created,
 * renamed and removed at the org scope only — while hours stay per scope like any schedule.
 */
export function useScheduleGroups(orgId: number | undefined): UseScheduleGroupsResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ScheduleInterface[]>([]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await API.Schedule.listAllSchedulesForOrg(orgId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = useMemo(() => scheduleGroupsFromRows(rows), [rows]);

  const addGroup = useCallback(
    async (name: string): Promise<{ ok: true; slug: string } | { ok: false; error: string }> => {
      if (!orgId) return { ok: false, error: "No organization." };
      const label = name.trim();
      const slug = slugifyScheduleGroupName(label);
      if (!slug) return { ok: false, error: "Enter a name with at least one letter or number." };
      if (groups.some((group) => group.slug === slug || group.label.toLowerCase() === label.toLowerCase())) {
        return { ok: false, error: `A schedule group named "${label}" already exists.` };
      }
      await API.Schedule.upsertSchedule(orgId, {
        groupId: null,
        locationId: null,
        slug,
        label,
        weekly: [...STANDARD_DEFAULT_WEEKLY],
        isActive: true
      });
      await refresh();
      return { ok: true, slug };
    },
    [orgId, groups, refresh]
  );

  const renameGroup = useCallback(
    async (slug: string, label: string): Promise<void> => {
      if (!orgId) return;
      await API.Schedule.upsertSchedule(orgId, { groupId: null, locationId: null, slug, label: label.trim() });
      await refresh();
    },
    [orgId, refresh]
  );

  const deleteGroup = useCallback(
    async (slug: string): Promise<void> => {
      if (!orgId) return;
      await API.Schedule.deleteScheduleGroup(orgId, slug);
      await refresh();
    },
    [orgId, refresh]
  );

  return { loading, error, groups, refresh, addGroup, renameGroup, deleteGroup };
}
